import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import styles from './DiscoverGlobe.module.css';
import PillarBar from './PillarBar';
import ActivityDrawer from './ActivityDrawer';
import { normalizeGuid } from '../../utils/guid';
import { verifyGlobePasscode } from '../../api/systemSettings';
import { earthVertexShader, earthFragmentShader, atmosVertexShader, atmosFragmentShader } from './shaders/earthShader';
import { starVertexShader, starFragmentShader, beamVertexShader, beamFragmentShader, bgVertexShader, bgFragmentShader } from './shaders/starfieldShader';
import { latLonToPosition } from './utils/geoCoords';
import { extractHotspots, extractCommunityHotspots, dedupeByProximity, HotspotData } from './utils/hotspots';
import { resolveEntityCoords } from './utils/communityGeoLookup';

// ── Theme-aware shader color helper ──
// Reads --globe-* CSS custom properties from :root and converts to THREE.Color.
// Called at init and whenever the theme changes.
function getGlobeTokenColor(token: string, fallback: string): THREE.Color {
  const val = getComputedStyle(document.documentElement).getPropertyValue(token).trim() || fallback;
  return new THREE.Color(val);
}

/** State for the hotspot click popup overlay. */
interface HotspotPopup {
  hotspot: HotspotData;
  screenX: number;
  screenY: number;
}

// ─── Texture URLs (NASA-derived via three-globe CDN) ───
const EARTH_DAY_URL = 'https://unpkg.com/three-globe@2.31.1/example/img/earth-blue-marble.jpg';
const EARTH_NIGHT_URL = 'https://unpkg.com/three-globe@2.31.1/example/img/earth-night.jpg';
const EARTH_BUMP_URL = 'https://unpkg.com/three-globe@2.31.1/example/img/earth-topology.png';

interface DiscoverGlobeProps {
  /** Projects in seed-data shape (project_GUID, location_lat, etc.) */
  projects: Record<string, any>[];
  /** Community entities from the API (seed-shape objects). */
  communities?: Record<string, any>[];
  /** Community parent-child relationships (for geographic name resolution). */
  communityRelationships?: Record<string, any>[];
  /** Whether the current user is a system admin (controls passcode input visibility). */
  isSystemAdmin?: boolean;
}

/**
 * Three.js 3D globe — Discover Now landing page.
 * Features: NASA-textured Earth, parallax starfield, atmosphere glow,
 * connection beams, project hotspot markers, five-pillar icon bar.
 *
 * Clicking the special black orb dissolves the globe, leaving only the starfield.
 */
export default function DiscoverGlobe({ projects, communities = [], communityRelationships = [], isSystemAdmin = false }: DiscoverGlobeProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [_loading, setLoading] = useState(true);  
  const [loadingHidden, setLoadingHidden] = useState(false);

  // When true, globe + overlay are hidden; only starfield + input remains
  const [starfieldOnly, setStarfieldOnly] = useState(false);
  // Fires at the START of the dissolve (for CSS exit animations on overlay elements)
  const [dissolveStarted, setDissolveStarted] = useState(false);
  const [orbInput, setOrbInput] = useState('');
  const [orbVerifying, setOrbVerifying] = useState(false); // API call in flight
  const [showMote, setShowMote] = useState(false);   // "So mote it be..." phrase mounted
  const [moteFading, setMoteFading] = useState(false); // fade-out in progress
  const [showSymbol, setShowSymbol] = useState(false);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const [hotspotPopup, setHotspotPopup] = useState<HotspotPopup | null>(null);

  // Store hotspots derived from projects + communities
  const hotspotsRef = useRef<HotspotData[]>([]);
  const projectHotspots = extractHotspots(projects, 20);
  const communityHotspots = extractCommunityHotspots(communities, 30, communityRelationships);
  // Communities first (country-level, higher priority), then projects
  hotspotsRef.current = dedupeByProximity([...communityHotspots, ...projectHotspots]);

  // Refs so the Three.js click handler can communicate with React
  const earthGroupRef = useRef<THREE.Group | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const canvasRectRef = useRef<DOMRect | null>(null);
  const onHotspotClickRef = useRef<(popup: HotspotPopup | null) => void>(() => {});
  onHotspotClickRef.current = setHotspotPopup;

  // Track previously-known active community GUIDs for pin-drop detection
  const prevActiveGuidsRef = useRef<Set<string>>(new Set());
  const activationQueueRef = useRef<{ lat: number; lon: number; startTime: number }[]>([]);

  // Ref so the Three.js click handler can trigger the React state change
  const onSpecialOrbClickRef = useRef(() => {});
  onSpecialOrbClickRef.current = () => setStarfieldOnly(true);

  // Fires at dissolve START (overlay CSS exit animations begin immediately)
  const onDissolveStartRef = useRef(() => {});
  onDissolveStartRef.current = () => {
    console.log('[DiscoverGlobe] Dissolve started — CSS exit animations triggered');
    setDissolveStarted(true);
  };

  // Ref for globe highlight from activity drawer
  const highlightTargetRef = useRef<{ lat: number; lon: number; startTime: number } | null>(null);
  // Ref for visual highlight effect (ripple + flash) on the selected hotspot
  const highlightedHotspotRef = useRef<{ id: string; startTime: number } | null>(null);

  // Detect newly activated communities and queue pin-drop animations
  useEffect(() => {
    const currentActive = new Set(
      communities
        .filter((c) => (c.is_active !== false && c.isActive !== false))
        .map((c) => c.entity_guid || c.entityGuid || c.id || "")
        .filter(Boolean)
    );
    const prev = prevActiveGuidsRef.current;

    // Only detect activations after the first load (prev has entries)
    if (prev.size > 0) {
      currentActive.forEach((guid) => {
        if (!prev.has(guid)) {
          // New activation! Resolve coords and queue animation
          const entity = communities.find(
            (c) => normalizeGuid(c.entity_guid || c.entityGuid || c.id) === normalizeGuid(guid)
          );
          if (entity) {
            const coords = resolveEntityCoords(entity, communities, communityRelationships);
            if (coords) {
              activationQueueRef.current.push({
                lat: coords.lat,
                lon: coords.lon,
                startTime: -1, // will be set in animate()
              });
            }
          }
        }
      });
    }

    prevActiveGuidsRef.current = currentActive;
    // DEV-946: communityRelationships is read inside resolveEntityCoords()
    // and was missing from the deps array. Without it, newly-arrived
    // relationships from the API would not trigger re-evaluation, leaving
    // pin-drop animations using stale coords for late-arriving entities.
  }, [communities, communityRelationships]);

  // ─── Reactive hotspot rebuild when data changes ───
  // The Three.js scene is built once, but hotspots need to update when
  // projects or communities arrive asynchronously from the API.
  useEffect(() => {
    const earthGroup = earthGroupRef.current;
    if (!earthGroup) return;

    const hotspots = hotspotsRef.current;

    // Remove existing hotspot meshes (identified by userData flags)
    const toRemove = earthGroup.children.filter(
      (c) => c.userData?.isHotspotDot || c.userData?.isHotspotRing
    );
    toRemove.forEach((c) => {
      earthGroup.remove(c);
      (c as THREE.Mesh).geometry?.dispose();
      ((c as THREE.Mesh).material as THREE.Material)?.dispose();
    });

    // RGB color cycle palette for project markers
    const hotspotColors = [
      new THREE.Color(0xD53541),
      new THREE.Color(0x45BB47),
      new THREE.Color(0x3399ff),
    ];
    const communityWhite = new THREE.Color(0xe0e0e0);

    // Re-add markers from the latest data
    hotspots.forEach((hs, idx) => {
      const pos = latLonToPosition(hs.lat, hs.lon, 1.01);
      const isCommunity = hs.entityKind === "community";

      const dotGeo = new THREE.SphereGeometry(isCommunity ? 0.022 : 0.018, 8, 8);
      const dotMat = new THREE.MeshBasicMaterial({
        color: isCommunity ? communityWhite : hotspotColors[0],
        transparent: true,
        opacity: isCommunity ? 0.85 : 0.9,
        blending: THREE.AdditiveBlending,
      });
      const dot = new THREE.Mesh(dotGeo, dotMat);
      dot.position.copy(pos);
      dot.userData = { isHotspotDot: true, hotspotData: hs, phaseOffset: idx * 0.7, entityKind: hs.entityKind };
      earthGroup.add(dot);

      const pRingGeo = new THREE.RingGeometry(
        isCommunity ? 0.03 : 0.025,
        isCommunity ? 0.05 : 0.04,
        32
      );
      const pRingMat = new THREE.MeshBasicMaterial({
        color: isCommunity ? communityWhite : hotspotColors[0],
        transparent: true,
        opacity: isCommunity ? 0.25 : 0.35,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const pRing = new THREE.Mesh(pRingGeo, pRingMat);
      pRing.position.copy(pos);
      pRing.lookAt(pos.clone().multiplyScalar(2));
      pRing.userData = { isHotspotRing: true, hotspotData: hs, baseScale: 1, phaseOffset: idx * 0.7, entityKind: hs.entityKind };
      earthGroup.add(pRing);
    });
     
  }, [projects, communities]);

  const initScene = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // ─── Renderer ───
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    const rect = container.getBoundingClientRect();
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, rect.width / rect.height, 0.1, 1000);
    camera.position.set(0, 0.3, 4.5);
    camera.lookAt(0, 0, 0);

    // Raycaster for orb click detection
    const raycaster = new THREE.Raycaster();

    // ─── Texture loader ───
    const loader = new THREE.TextureLoader();
    let texturesLoaded = 0;
    const totalTextures = 3;
    let earthDayTex: THREE.Texture;
    let earthNightTex: THREE.Texture;
    let earthBumpTex: THREE.Texture;

    function onTextureLoad() {
      texturesLoaded++;
      if (texturesLoaded >= totalTextures) {
        buildScene();
        setLoading(false);
        setTimeout(() => setLoadingHidden(true), 800);
      }
    }

    earthDayTex = loader.load(EARTH_DAY_URL, onTextureLoad, undefined, onTextureLoad);
    earthNightTex = loader.load(EARTH_NIGHT_URL, onTextureLoad, undefined, onTextureLoad);
    earthBumpTex = loader.load(EARTH_BUMP_URL, onTextureLoad, undefined, onTextureLoad);

    // ─── Starfield: 3-layer parallax ───
    function createStarLayer(count: number, minSize: number, maxSize: number, depth: number, depthSpread: number) {
      const geo = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const sizes = new Float32Array(count);

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const r = depth + Math.random() * depthSpread;
        positions[i3] = r * Math.sin(phi) * Math.cos(theta);
        positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        positions[i3 + 2] = r * Math.cos(phi);
        sizes[i] = minSize + Math.random() * (maxSize - minSize);
      }

      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

      const mat = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          uStarColor: { value: getGlobeTokenColor('--globe-star-color', '#ebeeff') },
        },
        vertexShader: starVertexShader,
        fragmentShader: starFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const points = new THREE.Points(geo, mat);
      return { points, mat };
    }

    const starFar = createStarLayer(1500, 0.6, 1.2, 28, 10);
    const starMid = createStarLayer(600, 1.0, 2.2, 18, 8);
    const starNear = createStarLayer(200, 1.8, 3.5, 10, 6);

    const starLayers = [
      { layer: starFar, speedY: 0.005, speedX: 0.002 },
      { layer: starMid, speedY: 0.012, speedX: 0.005 },
      { layer: starNear, speedY: 0.025, speedX: 0.01 },
    ];

    starLayers.forEach((s) => scene.add(s.layer.points));

    // ─── Background gradient (colors from theme tokens) ───
    const bgGeo = new THREE.PlaneGeometry(100, 100);
    const bgMat = new THREE.ShaderMaterial({
      uniforms: {
        uBgDark: { value: getGlobeTokenColor('--globe-bg-dark', '#020a1a') },
        uBgMid:  { value: getGlobeTokenColor('--globe-bg-mid', '#041247') },
      },
      vertexShader: bgVertexShader,
      fragmentShader: bgFragmentShader,
      depthWrite: false,
      depthTest: false,
    });
    const bgMesh = new THREE.Mesh(bgGeo, bgMat);
    bgMesh.renderOrder = -1;
    scene.add(bgMesh);

    // ─── Earth group (everything that disappears on orb click) ───
    const earthGroup = new THREE.Group();
    scene.add(earthGroup);
    earthGroupRef.current = earthGroup;
    cameraRef.current = camera;

    let earthMat: THREE.ShaderMaterial | null = null;
    let atmosMat: THREE.ShaderMaterial | null = null;
    const beams: THREE.Mesh[] = [];

    // Dissolve state — driven from the click handler, consumed in animate()
    let dissolving = false;
    let dissolveStart = 0;
    const dissolveDuration = 1.2; // seconds

    // ─── Build scene (called after textures load) ───
    function buildScene() {
      // Earth globe
      const earthGeo = new THREE.SphereGeometry(1.0, 64, 64);
      earthMat = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          dayMap: { value: earthDayTex },
          nightMap: { value: earthNightTex },
          bumpMap: { value: earthBumpTex },
          glowColor: { value: new THREE.Color(0x4df0ff) },
          sunDir: { value: new THREE.Vector3(1.5, 0.5, 1.0).normalize() },
        },
        vertexShader: earthVertexShader,
        fragmentShader: earthFragmentShader,
        transparent: false,
      });
      earthGroup.add(new THREE.Mesh(earthGeo, earthMat));

      // Thin wireframe overlay
      const wireGeo = new THREE.IcosahedronGeometry(1.003, 4);
      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x4df0ff,
        wireframe: true,
        transparent: true,
        opacity: 0.03,
        blending: THREE.AdditiveBlending,
      });
      earthGroup.add(new THREE.Mesh(wireGeo, wireMat));

      // Atmosphere glow
      const atmosGeo = new THREE.SphereGeometry(1.08, 64, 64);
      atmosMat = new THREE.ShaderMaterial({
        uniforms: {
          glowColor: { value: new THREE.Color(0x3ab8e0) },
          time: { value: 0 },
        },
        vertexShader: atmosVertexShader,
        fragmentShader: atmosFragmentShader,
        transparent: true,
        side: THREE.BackSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      earthGroup.add(new THREE.Mesh(atmosGeo, atmosMat));

      // Orbital ring
      const ringGeo = new THREE.RingGeometry(1.35 - 0.004, 1.35 + 0.004, 128);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0x4df0ff,
        transparent: true,
        opacity: 0.1,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = Math.PI / 2.2;
      earthGroup.add(ring);

      // Connection beams
      const beamCount = 6;
      for (let i = 0; i < beamCount; i++) {
        const tubeMat = new THREE.ShaderMaterial({
          uniforms: {
            time: { value: 0 },
            color: { value: new THREE.Color(0x4df0ff) },
            offset: { value: Math.random() * 10 },
          },
          vertexShader: beamVertexShader,
          fragmentShader: beamFragmentShader,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });

        const lat1 = (Math.random() - 0.5) * Math.PI;
        const lon1 = Math.random() * Math.PI * 2;
        const start = new THREE.Vector3(
          Math.cos(lat1) * Math.cos(lon1),
          Math.sin(lat1),
          Math.cos(lat1) * Math.sin(lon1)
        );
        const lat2 = (Math.random() - 0.5) * Math.PI;
        const lon2 = Math.random() * Math.PI * 2;
        const end = new THREE.Vector3(
          Math.cos(lat2) * Math.cos(lon2),
          Math.sin(lat2),
          Math.cos(lat2) * Math.sin(lon2)
        );
        const mid = start.clone().add(end).multiplyScalar(0.5);
        const height = 1.0 + start.distanceTo(end) * 0.5;
        mid.normalize().multiplyScalar(height);

        const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
        const tubeGeo = new THREE.TubeGeometry(curve, 44, 0.003, 4, false);
        const beam = new THREE.Mesh(tubeGeo, tubeMat);
        beams.push(beam);
        earthGroup.add(beam);
      }

      // Hotspot markers from real project data
      addHotspotMarkers();

      // ─── Special golden pulsing orb at 31.7781, 35.2359 ───
      const specialPos = latLonToPosition(31.7781, 35.2359, 1.015);
      const specialDotGeo = new THREE.SphereGeometry(0.0375, 24, 24); // 50% larger
      const specialDotMat = new THREE.MeshBasicMaterial({
        color: 0xd4a017,
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
      });
      const specialDot = new THREE.Mesh(specialDotGeo, specialDotMat);
      specialDot.position.copy(specialPos);
      specialDot.userData = { isSpecialOrb: true };
      earthGroup.add(specialDot);

      // Outer glow ring for the special orb (gold)
      const specialRingGeo = new THREE.RingGeometry(0.045, 0.075, 32);
      const specialRingMat = new THREE.MeshBasicMaterial({
        color: 0xd4a017,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const specialRing = new THREE.Mesh(specialRingGeo, specialRingMat);
      specialRing.position.copy(specialPos);
      specialRing.lookAt(specialPos.clone().multiplyScalar(2));
      specialRing.userData = { isSpecialRing: true };
      earthGroup.add(specialRing);
    }

    // RGB color cycle for hotspot dots and rings
    const hotspotColors = [
      new THREE.Color(0xD53541), // red
      new THREE.Color(0x45BB47), // green
      new THREE.Color(0x3399ff), // blue
    ];

    // Off-white color for community hotspots
    const communityWhite = new THREE.Color(0xe0e0e0);

    function addHotspotMarkers() {
      const hotspots = hotspotsRef.current;
      hotspots.forEach((hs, idx) => {
        const pos = latLonToPosition(hs.lat, hs.lon, 1.01);
        const isCommunity = hs.entityKind === "community";

        // Glowing dot — tagged for color cycling (projects) or steady white (communities)
        const dotGeo = new THREE.SphereGeometry(isCommunity ? 0.022 : 0.018, 8, 8);
        const dotMat = new THREE.MeshBasicMaterial({
          color: isCommunity ? communityWhite : hotspotColors[0],
          transparent: true,
          opacity: isCommunity ? 0.85 : 0.9,
          blending: THREE.AdditiveBlending,
        });
        const dot = new THREE.Mesh(dotGeo, dotMat);
        dot.position.copy(pos);
        dot.userData = { isHotspotDot: true, hotspotData: hs, phaseOffset: idx * 0.7, entityKind: hs.entityKind };
        earthGroup.add(dot);

        // Pulsing ring
        const pRingGeo = new THREE.RingGeometry(
          isCommunity ? 0.03 : 0.025,
          isCommunity ? 0.05 : 0.04,
          32
        );
        const pRingMat = new THREE.MeshBasicMaterial({
          color: isCommunity ? communityWhite : hotspotColors[0],
          transparent: true,
          opacity: isCommunity ? 0.25 : 0.35,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        const pRing = new THREE.Mesh(pRingGeo, pRingMat);
        pRing.position.copy(pos);
        pRing.lookAt(pos.clone().multiplyScalar(2));
        pRing.userData = { baseScale: 1, isHotspotRing: true, hotspotData: hs, phaseOffset: idx * 0.7, entityKind: hs.entityKind };
        earthGroup.add(pRing);
      });
    }

    // ─── Responsive globe positioning ───
    const globeTarget = { x: 0, y: 0 };
    const globeCurrent = { x: 0, y: 0 };

    function updateGlobeTarget() {
      const r = container.getBoundingClientRect();
      const aspect = r.width / r.height;
      if (aspect < 1.0) {
        const narrowness = Math.max(0, 1.0 - aspect);
        globeTarget.x = -0.8 * Math.min(narrowness * 2, 1.0);
        globeTarget.y = 0;
      } else {
        globeTarget.x = 0;
        globeTarget.y = 0;
      }
    }
    updateGlobeTarget();

    // ─── Mouse drag rotation ───
    const clock = new THREE.Clock();
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let userRotY = 0;
    let userRotX = 0.15;
    let dragBaseY = 0;
    let dragBaseX = 0;
    let autoRotOffset = 0;
    const autoRotSpeed = 0.08;

    function onMouseDown(e: MouseEvent) {
      isDragging = true;
      onHotspotClickRef.current(null); // dismiss popup on drag
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      const t = clock.getElapsedTime();
      userRotY = (t - autoRotOffset) * autoRotSpeed + userRotY;
      autoRotOffset = t;
      dragBaseY = userRotY;
      dragBaseX = userRotX;
      canvas.classList.add(styles.dragging);
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDragging) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      userRotY = dragBaseY + dx * 0.005;
      userRotX = Math.max(-1.2, Math.min(1.2, dragBaseX + dy * 0.005));
    }

    function endDrag() {
      if (!isDragging) return;
      isDragging = false;
      const t = clock.getElapsedTime();
      autoRotOffset = t - userRotY / autoRotSpeed;
      userRotY = 0;
      canvas.classList.remove(styles.dragging);
    }

    // Touch support
    function onTouchStart(e: TouchEvent) {
      if (e.touches.length === 1) {
        isDragging = true;
        dragStartX = e.touches[0].clientX;
        dragStartY = e.touches[0].clientY;
        const t = clock.getElapsedTime();
        userRotY = (t - autoRotOffset) * autoRotSpeed + userRotY;
        autoRotOffset = t;
        dragBaseY = userRotY;
        dragBaseX = userRotX;
      }
    }

    function onTouchMove(e: TouchEvent) {
      if (!isDragging || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - dragStartX;
      const dy = e.touches[0].clientY - dragStartY;
      userRotY = dragBaseY + dx * 0.005;
      userRotX = Math.max(-1.2, Math.min(1.2, dragBaseX + dy * 0.005));
    }

    // Click detection for the special orb (only fires if the mouse didn't drag)
    let clickStartX = 0;
    let clickStartY = 0;

    function onClickStart(e: MouseEvent) {
      clickStartX = e.clientX;
      clickStartY = e.clientY;
    }

    function onClickEnd(e: MouseEvent) {
      if (dissolving) return;
      const dx = Math.abs(e.clientX - clickStartX);
      const dy = Math.abs(e.clientY - clickStartY);
      if (dx > 5 || dy > 5) return;

      const r = canvas.getBoundingClientRect();
      canvasRectRef.current = r;
      const mouse = new THREE.Vector2(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1,
      );

      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(earthGroup.children, false);

      let handled = false;
      for (const hit of hits) {
        const ud = hit.object.userData;

        // Hotspot click → show popup
        if (ud?.isHotspotDot || ud?.isHotspotRing) {
          const hsData = ud.hotspotData as HotspotData | undefined;
          if (hsData) {
            // Project 3D position to screen coordinates
            const worldPos = hit.object.position.clone();
            earthGroup.localToWorld(worldPos);
            const projected = worldPos.project(camera);
            const screenX = (projected.x * 0.5 + 0.5) * r.width;
            const screenY = (-projected.y * 0.5 + 0.5) * r.height;
            onHotspotClickRef.current({ hotspot: hsData, screenX, screenY });
            handled = true;
          }
          break;
        }

        // Special orb click → dissolve
        if (ud?.isSpecialOrb || ud?.isSpecialRing) {
          dissolving = true;
          dissolveStart = clock.getElapsedTime();
          onDissolveStartRef.current();
          handled = true;
          break;
        }
      }

      // Clicking empty space dismisses popup
      if (!handled) {
        onHotspotClickRef.current(null);
      }
    }

    canvas.addEventListener('mousedown', onClickStart);
    canvas.addEventListener('mouseup', onClickEnd);

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', endDrag);
    canvas.addEventListener('mouseleave', endDrag);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', endDrag);

    // ─── Resize observer ───
    const resizeObserver = new ResizeObserver(() => {
      const r = container.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
      updateGlobeTarget();
    });
    resizeObserver.observe(container);

    // ─── Animation loop ───
    let animFrameId: number;

    function animate() {
      animFrameId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // ─── Dissolve transition ───
      if (dissolving) {
        const elapsed = t - dissolveStart;
        const progress = Math.min(elapsed / dissolveDuration, 1.0);
        // Ease-out cubic
        const ease = 1 - Math.pow(1 - progress, 3);

        // Scale globe down and fade all its children
        earthGroup.scale.setScalar(1 - ease * 0.5);
        earthGroup.children.forEach((child) => {
          const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial | THREE.ShaderMaterial;
          if (mat && 'opacity' in mat) {
            mat.transparent = true;
            mat.opacity = Math.max(0, 1 - ease);
          }
        });

        if (progress >= 1.0) {
          // Fully dissolved — remove earthGroup from scene, notify React
          scene.remove(earthGroup);
          dissolving = false;
          onSpecialOrbClickRef.current();
        }
      }

      // Smooth lerp globe position
      if (earthGroup.parent) {
        globeCurrent.x += (globeTarget.x - globeCurrent.x) * 0.03;
        globeCurrent.y += (globeTarget.y - globeCurrent.y) * 0.03;
        earthGroup.position.x = globeCurrent.x;
        earthGroup.position.y = globeCurrent.y;

        // Globe rotation
        if (isDragging) {
          earthGroup.rotation.y = userRotY;
          earthGroup.rotation.x = userRotX;
        } else {
          earthGroup.rotation.y = (t - autoRotOffset) * autoRotSpeed + userRotY;
          userRotX += (0.15 - userRotX) * 0.02;
          earthGroup.rotation.x = userRotX;
        }
      }

      // Star parallax
      starLayers.forEach((s) => {
        s.layer.points.rotation.y = t * s.speedY;
        s.layer.points.rotation.x = t * s.speedX;
        s.layer.mat.uniforms.time.value = t;
      });

      // Update shader uniforms (only while globe is in the scene)
      if (earthGroup.parent) {
        if (earthMat) earthMat.uniforms.time.value = t;
        if (atmosMat) atmosMat.uniforms.time.value = t;

        beams.forEach((b) => {
          (b.material as THREE.ShaderMaterial).uniforms.time.value = t;
        });

        // Pulse the special golden orb with metallic shimmer
        earthGroup.children.forEach((child) => {
          if (child.userData?.isSpecialOrb) {
            const pulse = 1.0 + Math.sin(t * 2.5) * 0.3;
            child.scale.setScalar(pulse);
            // Shimmer between deep gold and bright gold
            const shimmer = 0.7 + Math.sin(t * 4.0) * 0.3;
            const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
            mat.color.setRGB(shimmer * 0.83, shimmer * 0.63, shimmer * 0.09);
          }
          if (child.userData?.isSpecialRing) {
            const pulse = 1.0 + Math.sin(t * 2.5) * 0.5;
            child.scale.setScalar(pulse);
            const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
            mat.opacity = 0.5 * (1.5 - pulse * 0.5);
            const shimmer = 0.6 + Math.sin(t * 4.0 + 0.5) * 0.4;
            mat.color.setRGB(shimmer * 0.83, shimmer * 0.63, shimmer * 0.09);
          }
        });

        // Animate hotspot dots & rings — projects cycle RGB, communities glow white
        earthGroup.children.forEach((child) => {
          const ud = child.userData;
          if (!ud) return;
          const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;

          if (ud.isHotspotDot) {
            if (ud.entityKind === "community") {
              // Communities: gentle brightness pulse, fixed off-white
              const brightness = 0.8 + Math.sin(t * 1.5 + (ud.phaseOffset || 0)) * 0.2;
              mat.color.setRGB(brightness, brightness, brightness * 0.97);
              mat.opacity = 0.7 + Math.sin(t * 1.2 + (ud.phaseOffset || 0)) * 0.15;
            } else {
              // Projects: cycle red → green → blue
              const speed = 0.6;
              const phase = (t * speed + (ud.phaseOffset || 0)) % 3;
              const idx = Math.floor(phase);
              const frac = phase - idx;
              const from = hotspotColors[idx];
              const to = hotspotColors[(idx + 1) % 3];
              mat.color.copy(from).lerp(to, frac);
            }
          }

          if (ud.isHotspotRing) {
            if (ud.entityKind === "community") {
              // Communities: slower, gentler pulse with fixed white
              const pulse = 1.0 + Math.sin(t * 1.8 + child.position.x * 3) * 0.3;
              child.scale.setScalar(pulse);
              mat.opacity = 0.2 * (1.5 - pulse * 0.4);
              const brightness = 0.85 + Math.sin(t * 1.5 + (ud.phaseOffset || 0)) * 0.15;
              mat.color.setRGB(brightness, brightness, brightness * 0.97);
            } else {
              // Projects: existing pulsing RGB rings
              const pulse = 1.0 + Math.sin(t * 3 + child.position.x * 5) * 0.4;
              child.scale.setScalar(pulse);
              mat.opacity = 0.35 * (1.5 - pulse * 0.5);

              const speed = 0.6;
              const phase = (t * speed + (ud.phaseOffset || 0)) % 3;
              const idx = Math.floor(phase);
              const frac = phase - idx;
              const from = hotspotColors[idx];
              const to = hotspotColors[(idx + 1) % 3];
              mat.color.copy(from).lerp(to, frac);
            }
          }
        });

        // ─── Pin-drop animation for newly activated communities ───
        const queue = activationQueueRef.current;
        for (let qi = queue.length - 1; qi >= 0; qi--) {
          const act = queue[qi];
          if (act.startTime < 0) {
            act.startTime = t;
            // Spawn pin + 3 ripple rings
            const pinPos = latLonToPosition(act.lat, act.lon, 1.02);
            const pinGeo = new THREE.SphereGeometry(0.028, 12, 12);
            const pinMat = new THREE.MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 1.0,
              blending: THREE.AdditiveBlending,
            });
            const pin = new THREE.Mesh(pinGeo, pinMat);
            pin.position.copy(pinPos);
            pin.scale.setScalar(0);
            pin.userData = { isPinDrop: true, pinStartTime: t };
            earthGroup.add(pin);

            for (let ri = 0; ri < 3; ri++) {
              const ripGeo = new THREE.RingGeometry(0.02, 0.04, 32);
              const ripMat = new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0,
                side: THREE.DoubleSide,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
              });
              const rip = new THREE.Mesh(ripGeo, ripMat);
              rip.position.copy(pinPos);
              rip.lookAt(pinPos.clone().multiplyScalar(2));
              rip.userData = { isPinRipple: true, rippleIndex: ri, pinStartTime: t };
              earthGroup.add(rip);
            }
          }

          // Animate pin + ripples
          const elapsed = t - act.startTime;
          if (elapsed > 3.5) {
            // Animation done — remove temp meshes, add permanent community hotspot
            earthGroup.children
              .filter((c) => c.userData?.pinStartTime === act.startTime)
              .forEach((c) => {
                earthGroup.remove(c);
                (c as THREE.Mesh).geometry?.dispose();
                ((c as THREE.Mesh).material as THREE.Material)?.dispose();
              });
            queue.splice(qi, 1);
          }
        }

        // Animate active pin-drop meshes
        earthGroup.children.forEach((child) => {
          const ud = child.userData;
          if (!ud) return;
          const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;

          if (ud.isPinDrop) {
            const e = t - ud.pinStartTime;
            if (e < 1.2) {
              // Bounce-in scale: easeOutBounce
              const p = Math.min(e / 0.8, 1);
              const bounce = p < 0.36 ? 7.5625 * p * p
                : p < 0.73 ? 7.5625 * (p - 0.545) * (p - 0.545) + 0.75
                : p < 0.91 ? 7.5625 * (p - 0.82) * (p - 0.82) + 0.9375
                : 7.5625 * (p - 0.955) * (p - 0.955) + 0.984375;
              child.scale.setScalar(bounce);
              mat.opacity = 1.0;
            } else if (e > 2.5) {
              // Fade to normal community hotspot appearance
              const fadeP = Math.min((e - 2.5) / 1.0, 1);
              mat.opacity = 1.0 - fadeP * 0.15;
              child.scale.setScalar(1.0 - fadeP * 0.15 + 0.85 * fadeP);
            }
          }

          if (ud.isPinRipple) {
            const delay = 0.8 + ud.rippleIndex * 0.3;
            const e = t - ud.pinStartTime - delay;
            if (e > 0 && e < 1.7) {
              const p = e / 1.7;
              child.scale.setScalar(1 + p * 2.5);
              mat.opacity = 0.6 * (1 - p);
            } else if (e >= 1.7) {
              mat.opacity = 0;
            }
          }
        });

        // ─── Globe highlight (from activity drawer click) ───
        // Smoothly rotates the globe so the target longitude faces the camera.
        //
        // The Three.js SphereGeometry places lon=0° facing the camera (-Z) at
        // rotation.y=0. Positive rotation.y spins the globe eastward (right-hand
        // rule around Y). To face longitude L toward the camera:
        //   targetRotY = -L * π/180
        //
        // rotation.y accumulates continuously from auto-rotation, so we normalize
        // both current and target to find the shortest angular path.
        const hl = highlightTargetRef.current;
        if (hl && !isDragging) {
          if (hl.startTime < 0) hl.startTime = t;
          const elapsed = t - hl.startTime;
          if (elapsed < 2.5) {
            const TWO_PI = Math.PI * 2;
            // Target rotation to face this longitude toward camera
            const targetBase = -hl.lon * (Math.PI / 180) - Math.PI / 2;
            // Normalize both to 0..2π to compute shortest-path difference
            const currentRotY = earthGroup.rotation.y;
            const currentMod = ((currentRotY % TWO_PI) + TWO_PI) % TWO_PI;
            const targetMod = ((targetBase % TWO_PI) + TWO_PI) % TWO_PI;
            // Shortest angular difference
            let diff = targetMod - currentMod;
            if (diff > Math.PI) diff -= TWO_PI;
            if (diff < -Math.PI) diff += TWO_PI;
            // Apply diff to actual (unwrapped) rotation
            const targetRotY = currentRotY + diff;
            // Ease-out interpolation
            const speed = Math.max(0.03, 0.10 * (1 - elapsed / 2.5));
            earthGroup.rotation.y += (targetRotY - currentRotY) * speed;
            // Sync auto-rotation so it doesn't fight after highlight ends
            userRotY = 0;
            autoRotOffset = t - earthGroup.rotation.y / autoRotSpeed;
          } else {
            highlightTargetRef.current = null;
          }
        }

        // ─── Hotspot visual highlight (ripple rings + color flash) ───
        const hlHs = highlightedHotspotRef.current;
        if (hlHs && earthGroup) {
          // On first frame: spawn 3 ripple rings at the hotspot's position
          if (hlHs.startTime < 0) {
            hlHs.startTime = t;
            // Find the hotspot dot closest to the highlight target coordinates.
            // We can't match by exact ID because dedupeByProximity may have merged
            // nearby communities, and sub-communities resolve to parent country coords.
            const hl = highlightTargetRef.current;
            const targetLat = hl?.lat ?? 0;
            const targetLon = hl?.lon ?? 0;
            const targetPos = latLonToPosition(targetLat, targetLon, 1.01);
            let targetDot: THREE.Object3D | undefined;
            let bestDist = Infinity;
            earthGroup.children.forEach((c) => {
              if (!c.userData?.isHotspotDot) return;
              const d = c.position.distanceTo(targetPos);
              if (d < bestDist) {
                bestDist = d;
                targetDot = c;
              }
            });
            // Only highlight if the nearest dot is reasonably close (within ~15° on globe)
            if (bestDist > 0.3) targetDot = undefined;
            if (targetDot) {
              const pos = targetDot.position.clone();
              const highlightColor = getGlobeTokenColor('--globe-glow', '#4df0ff');
              for (let ri = 0; ri < 3; ri++) {
                const ripGeo = new THREE.RingGeometry(0.02, 0.045, 32);
                const ripMat = new THREE.MeshBasicMaterial({
                  color: highlightColor,
                  transparent: true,
                  opacity: 0,
                  side: THREE.DoubleSide,
                  blending: THREE.AdditiveBlending,
                  depthWrite: false,
                });
                const rip = new THREE.Mesh(ripGeo, ripMat);
                rip.position.copy(pos);
                rip.lookAt(pos.clone().multiplyScalar(2));
                rip.userData = { isHighlightRipple: true, rippleIndex: ri, hlStartTime: t, hlId: hlHs.id };
                earthGroup.add(rip);
              }
            }
          }

          const hlElapsed = t - hlHs.startTime;
          const hlDuration = 3.0;

          // Color flash: override the target hotspot dot color to cyan/glow
          earthGroup.children.forEach((child) => {
            const ud = child.userData;
            if (!ud) return;
            const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;

            // Flash the dot color
            if (ud.isHotspotDot && (ud.hotspotData as HotspotData)?.id === hlHs.id) {
              if (hlElapsed < hlDuration) {
                const flashIntensity = Math.max(0, 1 - hlElapsed / hlDuration);
                const glowColor = getGlobeTokenColor('--globe-glow', '#4df0ff');
                const originalWhite = new THREE.Color(0.85, 0.85, 0.83);
                mat.color.copy(originalWhite).lerp(glowColor, flashIntensity);
                mat.opacity = 0.7 + flashIntensity * 0.3;
                // Scale pulse: grow then shrink back
                const scalePulse = 1 + Math.sin(hlElapsed * 4) * 0.3 * flashIntensity;
                child.scale.setScalar(scalePulse);
              } else {
                child.scale.setScalar(1);
              }
            }

            // Animate ripple rings: stagger outward expansion with fade
            if (ud.isHighlightRipple && ud.hlId === hlHs.id) {
              const delay = ud.rippleIndex * 0.25;
              const re = t - ud.hlStartTime - delay;
              if (re > 0 && re < 1.8) {
                const p = re / 1.8;
                child.scale.setScalar(1 + p * 3.0);
                mat.opacity = 0.7 * (1 - p);
              } else if (re >= 1.8) {
                mat.opacity = 0;
              }
            }
          });

          // Cleanup when done
          if (hlElapsed > hlDuration) {
            // Remove ripple meshes
            const ripples = earthGroup.children.filter(
              (c) => c.userData?.isHighlightRipple && c.userData.hlId === hlHs.id
            );
            ripples.forEach((c) => {
              earthGroup.remove(c);
              (c as THREE.Mesh).geometry?.dispose();
              ((c as THREE.Mesh).material as THREE.Material)?.dispose();
            });
            highlightedHotspotRef.current = null;
          }
        }
      }

      renderer.render(scene, camera);
    }

    animate();

    // ─── Theme change listener ───
    // When the ThemeProvider re-applies tokens to :root, update shader uniforms.
    const themeObserver = new MutationObserver(() => {
      bgMat.uniforms.uBgDark.value = getGlobeTokenColor('--globe-bg-dark', '#020a1a');
      bgMat.uniforms.uBgMid.value = getGlobeTokenColor('--globe-bg-mid', '#041247');
      const newStarColor = getGlobeTokenColor('--globe-star-color', '#ebeeff');
      starLayers.forEach(s => {
        s.layer.mat.uniforms.uStarColor.value = newStarColor;
      });
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    // ─── Cleanup ───
    return () => {
      themeObserver.disconnect();
      cancelAnimationFrame(animFrameId);
      resizeObserver.disconnect();

      canvas.removeEventListener('mousedown', onClickStart);
      canvas.removeEventListener('mouseup', onClickEnd);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', endDrag);
      canvas.removeEventListener('mouseleave', endDrag);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', endDrag);

      // Dispose Three.js resources
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry.dispose();
        if ((obj as THREE.Mesh).material) {
          const mat = (obj as THREE.Mesh).material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else {
            (mat as THREE.Material).dispose();
          }
        }
      });

      earthDayTex?.dispose();
      earthNightTex?.dispose();
      earthBumpTex?.dispose();
      renderer.dispose();
      earthGroupRef.current = null;
      cameraRef.current = null;
    };
     
  }, []);

  useEffect(() => {
    const cleanup = initScene();
    return cleanup;
  }, [initScene]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={`${styles.loading} ${loadingHidden ? styles.loadingHidden : ''}`}
        style={loadingHidden ? { display: 'none' } : undefined}
      >
        LOADING EARTH...
      </div>
      <canvas ref={canvasRef} className={`${styles.canvas}${activityDrawerOpen ? ` ${styles.canvasShifted}` : ''}`} />

      {/* Hotspot click popup */}
      {hotspotPopup && !starfieldOnly && (
        <div
          className={styles.hotspotPopup}
          style={{
            left: Math.min(hotspotPopup.screenX, (containerRef.current?.clientWidth ?? 400) - 200),
            top: Math.max(hotspotPopup.screenY - 80, 8),
          }}
        >
          <div className={styles.hotspotPopupName}>{hotspotPopup.hotspot.name}</div>
          <div className={styles.hotspotPopupType}>
            {hotspotPopup.hotspot.entityKind === "community" ? "Community" : "Project"}
          </div>
          <button
            className={styles.hotspotPopupBtn}
            onClick={() => {
              const hs = hotspotPopup.hotspot;
              const path = hs.entityKind === "community"
                ? `/communities/${hs.id}`
                : `/projects/${hs.id}`;
              setHotspotPopup(null);
              navigate(path);
            }}
          >
            View &rarr;
          </button>
        </div>
      )}

      {/* Scan line + overlay visible while globe is showing */}
      {!starfieldOnly && (
        <>
          <div className={`${styles.scanLine} ${dissolveStarted ? styles.scanLineExit : ''}`} />
          <div className={`${styles.overlay} ${dissolveStarted ? styles.overlayExit : ''}`}>
            <div className={`${styles.title} ${activityDrawerOpen ? styles.titleShifted : ''} ${dissolveStarted ? styles.titleExit : ''}`}>
              <h1 className={styles.titleH1}>Discover Now</h1>
              <p className={styles.titleSub}>Life4Earth</p>
            </div>
            <PillarBar dissolving={dissolveStarted} drawerOpen={activityDrawerOpen} />
          </div>
          <div className={dissolveStarted ? styles.drawerExit : ''}>
            <ActivityDrawer
              communities={communities}
              communityRelationships={communityRelationships}
              projects={projects}
              onHighlight={(lat, lon, entityId) => {
                highlightTargetRef.current = { lat, lon, startTime: -1 };
                if (entityId) highlightedHotspotRef.current = { id: entityId, startTime: -1 };
              }}
              onOpenChange={setActivityDrawerOpen}
            />
          </div>
        </>
      )}

      {/* Input field shown after the globe dissolves */}
      {starfieldOnly && (
        <div className={styles.starfieldOverlay}>
          {showMote ? (
            <div className={`${styles.motePhrase} ${moteFading ? styles.moteFadeOut : ''}`}>
              So mote it be<span className={styles.moteDots}>...</span>
            </div>
          ) : !showSymbol && isSystemAdmin ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const value = orbInput.trim();
                if (!value || orbVerifying) return;
                setOrbVerifying(true);
                try {
                  const { valid } = await verifyGlobePasscode(value);
                  if (valid) {
                    // Show "So mote it be..." phrase, then reveal symbol
                    setShowMote(true);
                    setMoteFading(false);
                    setTimeout(() => {
                      setMoteFading(true);
                      setTimeout(() => {
                        setShowMote(false);
                        setMoteFading(false);
                        setShowSymbol(true);
                      }, 1000);
                    }, 4000);
                  } else {
                    setShowSymbol(true);
                  }
                } catch (err) {
                  console.warn('[DiscoverGlobe] Passcode verify failed:', err);
                  setShowSymbol(true);
                } finally {
                  setOrbVerifying(false);
                }
              }}
              className={styles.orbForm}
            >
              <input
                type="text"
                value={orbInput}
                onChange={(e) => setOrbInput(e.target.value)}
                placeholder="Enter value..."
                className={styles.orbInput}
                autoFocus
                disabled={orbVerifying}
              />
              <button type="submit" className={styles.orbButton} disabled={orbVerifying}>
                {orbVerifying ? '...' : 'Enter'}
              </button>
            </form>
          ) : showSymbol ? (
            <div className={styles.symbolContainer}>
              <svg
                className={styles.symbolSvg}
                width="240"
                height="240"
                viewBox="0 0 180 180"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Outer circle */}
                <circle cx="90" cy="90" r="65" stroke="rgba(77,240,255,0.5)" strokeWidth="1.8" fill="none" className={styles.symbolCircleOuter} />
                {/* Inner circle */}
                <circle cx="90" cy="90" r="45" stroke="rgba(77,240,255,0.2)" strokeWidth="1" fill="none" className={styles.symbolCircleInner} />
                {/* Center point */}
                <circle cx="90" cy="90" r="4" fill="rgba(200,170,110,0.7)" className={styles.symbolCenter} />
                <circle cx="90" cy="90" r="8" stroke="rgba(200,170,110,0.25)" strokeWidth="0.8" fill="none" />
                {/* Two parallel lines (Saints John) */}
                <line x1="22" y1="40" x2="22" y2="140" stroke="rgba(77,240,255,0.4)" strokeWidth="1.5" strokeLinecap="round" className={styles.symbolLine} />
                <line x1="158" y1="40" x2="158" y2="140" stroke="rgba(77,240,255,0.4)" strokeWidth="1.5" strokeLinecap="round" className={styles.symbolLine} />
                {/* Cross-hairs */}
                <line x1="25" y1="90" x2="155" y2="90" stroke="rgba(77,240,255,0.1)" strokeWidth="0.8" />
                <line x1="90" y1="25" x2="90" y2="155" stroke="rgba(77,240,255,0.1)" strokeWidth="0.8" />
              </svg>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
